import React, { useMemo } from "react";
import { isApplicationStepOptional, isEmpty } from "utils";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { useAssessment, useFormData } from "hooks";
import { APPLICATION_STEPS, renderAssessmentAnswer } from "constants/index";
import { Grid } from "components/Containers";
import Card from "components/Card";
import DisplayBox from "components/DisplayBox";
import ApplicationQuestionnaireForm from "./ApplicationQuestionnaireForm";
import { TagComplete, TagIncomplete, TagOptional } from "components/Tags";

/**
 * ApplicationEditableQuestionnaire
 *
 * @param {Object}    missionRoleApplicant
 * @param {Boolean}   questionnaireEditing
 * @param {Function}  setQuestionnaireEditing
 */
const ApplicationEditableQuestionnaire = ({
  missionRoleApplicant,
  questionnaireEditing,
  setQuestionnaireEditing,
  ...props
}) => {
  const missionRole = missionRoleApplicant?.missionRole;
  const isOptional = isApplicationStepOptional(missionRole?.applicationSteps, APPLICATION_STEPS.questionnaire);
  const sectionCompleted = missionRoleApplicant?.applicationChecklist?.find(
    (step) => step.name === APPLICATION_STEPS.questionnaire
  )?.fulfilled;

  const { data, loading } = useFormData(
    {
      variables: {
        id: missionRole?.assessmentId,
        resourceId: missionRoleApplicant?.profile?.id,
      },
    },
    "questions"
  );

  const {
    data: answersData,
    loading: answersLoading,
    refetch,
  } = useAssessment({
    id: missionRole?.assessmentId,
    resourceId: missionRoleApplicant?.profile?.id,
  });

  const formData = useMemo(() => DATA(answersData), [answersData]);

  if (loading || answersLoading || !formData.length) return null;

  return (
    <>
      <Grid.col start={1} end={13}>
        <Card>
          <DisplayBox
            title={<FormattedMessage id="Missions.Applications.ApplicationEditableQuestionnaire.Title" />}
            description={<FormattedMessage id="Missions.Applications.ApplicationEditableQuestionnaire.Description" />}
            subtitle={!sectionCompleted ? isOptional ? <TagOptional /> : <TagIncomplete /> : <TagComplete />}
            editButtonText={<FormattedMessage id="Missions.Applications.ApplicationEditableQuestionnaire.Button" />}
            isEmpty={isEmpty(answersData?.answers)}
            numColumns={1}
            data={formData}
            onEdit={() => setQuestionnaireEditing(true)}
            onAdd={() => setQuestionnaireEditing(true)}
            {...props}
          />
        </Card>
      </Grid.col>

      {questionnaireEditing && (
        <ApplicationQuestionnaireForm
          questionnaire={data}
          missionRoleApplicant={missionRoleApplicant}
          answers={answersData?.answers}
          refetch={refetch}
          show={questionnaireEditing}
          onClose={() => setQuestionnaireEditing(false)}
        />
      )}
    </>
  );
};

const DATA = (answersData) =>
  (answersData?.questions || []).map(({ wording, type, options, properties }, index) => ({
    title: `Q${index + 1}: ${wording}`,
    body: renderAssessmentAnswer(answersData?.answers?.[properties?.name], { type, options, properties }),
  }));

ApplicationEditableQuestionnaire.propTypes = {
  missionRoleApplicant: PropTypes.object.isRequired,
  questionnaireEditing: PropTypes.bool,
  setQuestionnaireEditing: PropTypes.func,
};

export default ApplicationEditableQuestionnaire;
